import type { CardResult } from '../../../shared/types';

/**
 * Sessions that finished while the network did not.
 *
 * The drill already posts once, at the end, rather than card by card — which
 * means an offline session is exactly one failed request, not forty. That makes
 * surviving a dropped connection cheap: keep the payload, hand the learner
 * their summary, and send it when there is a connection again.
 *
 * Without this, walking into a tunnel loses the session outright. The summary
 * says "could not save" and the only recovery is to sit there and retry, which
 * is no recovery at all on a train.
 *
 * Kept in `localStorage` rather than memory on purpose: the point is to survive
 * the tab being closed, which is what actually happens when a page stops
 * working. It is per-device and per-browser, which is correct — an unsent
 * session belongs to the machine that recorded it.
 */

const KEY = 'pendingSessions.v1';
/**
 * Beyond this, the oldest are dropped. A queue that grows without limit on a
 * machine that is never online again would eventually fill the storage quota
 * and break settings and everything else that shares it.
 */
const MAX_QUEUED = 50;

export interface PendingSession {
  /** Identifies the retry, so a double flush cannot post it twice. */
  id: string;
  /** The section drilled, or null for a run that spanned sections. */
  sectionId: string | null;
  results: { cardId: string; result: CardResult }[];
  /** Captured when the session ended, not when it is finally sent. */
  tzOffsetMinutes: number;
  queuedAt: number;
}

function read(): PendingSession[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Anything malformed is dropped rather than crashing the flush — a corrupt
    // entry must not take the whole queue with it.
    return parsed.filter(
      (s): s is PendingSession =>
        Boolean(s) &&
        typeof s === 'object' &&
        typeof (s as PendingSession).id === 'string' &&
        Array.isArray((s as PendingSession).results),
    );
  } catch {
    return [];
  }
}

function write(sessions: PendingSession[]): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(KEY, JSON.stringify(sessions.slice(-MAX_QUEUED)));
  } catch {
    // A full quota is not worth interrupting the summary screen for.
  }
}

export function queueSession(
  session: Omit<PendingSession, 'id' | 'queuedAt'>,
): PendingSession {
  const entry: PendingSession = {
    ...session,
    id:
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    queuedAt: Date.now(),
  };
  write([...read(), entry]);
  return entry;
}

export function pendingCount(): number {
  return read().length;
}

export function listPending(): PendingSession[] {
  return read();
}

function remove(id: string): void {
  write(read().filter((s) => s.id !== id));
}

/**
 * Tries to send everything queued, oldest first.
 *
 * A session is removed only once the server has accepted it. A send that fails
 * stops the flush rather than carrying on down the queue: if one request failed
 * because the connection is gone, the rest will too, and hammering them all
 * just burns battery. Returns how many were accepted.
 */
export async function flushPending(
  send: (session: PendingSession) => Promise<unknown>,
): Promise<number> {
  let sent = 0;
  for (const session of read()) {
    try {
      await send(session);
      remove(session.id);
      sent += 1;
    } catch {
      break;
    }
  }
  return sent;
}
